import { Component } from '@angular/core';
import { IonicPage, ModalController, NavController, NavParams, Platform, ToastController } from 'ionic-angular';
import { OperationCommentsPage } from '../operation-comments/operation-comments';
import { OperationService } from '../../../services/operations/operation/operation.service';
import {
  DailyLogOperationInstance,
  DailyLogType,
  LogComment,
  OperationType,
  ProjectSpecificOperationSummary,
  ResponsibilityType,
  StepInstance,
  TypeSwitchGroup
} from '../../../models/projects/operation.models';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { DailyLogModalPage } from '../../daily-log/daily-log-modal/daily-log-modal';
import { DateService } from '../../../services/date/date.service';
import { OperationTimeService } from '../../../services/operations/operation-time/operation-time.service';
import { StepCommentsPage } from '../step-comments/step-comments';
import { OperationGenericCommentsPage } from '../operation-generic-comments/operation-generic-comments';
import { OperationValidatorService } from '../../../services/operations/operation-validator.service';
import { ActionType, ErrorType, ValidationResult } from '../../../models/projects/validation.models';
import { ErrorsComponent } from '../../shared/components/errors/errors.component';
import { NavHelperService } from '../../../services/nav/nav-helper.service';
import { RemoveCommentComponent } from '../remove-comment/remove-comment.component';
import { AffectedOperationsPage } from '../affected-operations/affected-operations';
import { NavMainMenuPage } from '../../nav/nav-menus/nav-main-menu/nav-main-menu';
import { DailyLogPage } from '../../daily-log/daily-log/daily-log';
import { MultipleDayService } from '../../../services/mutiple-day/multiple-day.service';
import { OperationDetailsLocationSelectPage } from '../operation-details-location-select/operation-details-location-select';
import { StepService } from '../../../services/step/step.service';
import { cloneDeep } from 'lodash';
import { OperationSaveService } from '../../../services/operations/operation-save/operation-save.service';
import { OperationSplitPage } from '../operation-split/operation-split';
import { AppData, OperationInstanceSaveRequest } from '../../../models/app-data/app-data';
import { AppDataSyncService } from '../../../services/app-data-sync/app-data-sync.service';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { DateParserService } from '../../../services/daily-log/daily-log-time/date-parser-service';
import { LocalOperationSaveService } from '../../../services/local/operation/local-operation-save.service';
import { TimeZoneService } from '../../../services/time-zone/time-zone.service';
import { JackingActivityService } from '../../../services/jacking-activity/jacking-activity.service';
import { FieldPositioningService } from '../../../services/field-positioning/field-positioning.service';
import { OnSiteMovementService } from '../../../services/on-site-movement/on-site-movement.service';
import { DailyLogValidationService } from '../../../services/daily-log/daily-log-validation/daily-log-validation.service';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { LockedDayPermissions } from '../../../models/permissions/permission.models';
import { CompleteDayService } from '../../../services/complete-day/complete-day.service';
import { AppConfig } from '../../../app/app.config';
import { DateTime } from 'luxon';
import { SupportContextKey } from '../../../models/support/support.models';
import { SupportContextService } from '../../../services/support/support-context.service';

@IonicPage()
@Component({
  selector: 'page-operation-details',
  templateUrl: 'operation-details.html',
})
export class OperationDetailsPage {
  config = AppConfig.settings;
  operation: ProjectSpecificOperationSummary = new ProjectSpecificOperationSummary();
  operationInstance: DailyLogOperationInstance = new DailyLogOperationInstance();

  firstLoadComplete: boolean;
  firstTime: boolean;

  saved: boolean = false;
  validationResult: ValidationResult = new ValidationResult();
  saveValidationIsValid: boolean = true;
  private operationValidationSettings: ProjectSpecificOperationSummary;
  responsibilityType = ResponsibilityType;
  responsibilitySelectOptions = {cssClass: 'responsibility-select-popover'};
  responsibilityValidationAttempted: boolean = false;
  mostRecentTime: PtLuxonDate;
  popFn = null;
  isWeb: boolean;
  lastStepPosition: number = null;
  loading: boolean = true;
  type: OperationType = OperationType.Contractual;
  public mask: Array<string | RegExp> = [/[0-2]/, /\d/, ':', /[0-5]/, /\d/];
  interruption = OperationType.Interruption;
  delay = OperationType.Delay;

  dailyLogDate: PtLuxonDate;
  endOfDay: PtLuxonDate;

  badgesLoaded: boolean = false;

  swapMessage = '';

  min: DateTime = DateTime.local();
  max: DateTime = DateTime.local();

  multipleDaysExist: boolean = false;
  dayCount = 0;
  // used for when adding an operation that defaults to start on the next day than the current daily log day
  differentDayOperation: boolean = false;
  canConfirmOperationTimes: boolean;

  lockedDayPermissions: LockedDayPermissions;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public platform: Platform,
    public operationService: OperationService,
    public operationTimeService: OperationTimeService,
    public operationValidatorService: OperationValidatorService,
    public dateService: DateService,
    public modalController: ModalController,
    public navHelperService: NavHelperService,
    public multipleDayService: MultipleDayService,
    public operationSaveService: OperationSaveService,
    public appDataSyncService: AppDataSyncService,
    public stepService: StepService,
    public dailyLogService: DailyLogService,
    private dailyLogValidationService: DailyLogValidationService,
    public dateParserService: DateParserService,
    private localOperationSaveService: LocalOperationSaveService,
    private toastCtrl: ToastController,
    private timeZoneService: TimeZoneService,
    private jackingActivitySerivce: JackingActivityService,
    private fieldPositioningService: FieldPositioningService,
    private onSiteMovementService: OnSiteMovementService,
    private permissionService: PermissionsService,
    private completeDayService: CompleteDayService,
    private supportContextService: SupportContextService,
  ) {
    this.dailyLogDate = navParams.get('dailyLogDate');
    this.lockedDayPermissions = new LockedDayPermissions();
  }

  async ionViewDidEnter() {
    this.supportContextService.setComponentContext(SupportContextKey.AddToDailyLog, 'Operation Add', 'OperationDetailsPage');

    // we want to call initViewLoad() every time the view loads, and initFirstLoad() only the first time
    // these used to be: initFirstLoad() == ionViewDidLoad() and initViewEnter() == ionViewDidEnter(), but we needed to replace the ionic lifecycle
    // events to ensure that ionViewDidEnter() always runs only after ionViewDidLoad() (was causing null reference sometimes otherwise)
    if (!this.firstLoadComplete) {
      await this.initFirstLoad();
      await this.initViewEnter();
      this.firstLoadComplete = true;
    } else {
      await this.initViewEnter();
    }

    await this.refreshOperationValidationSettings();
  }

  async initFirstLoad() {
    this.loading = true;
    this.isWeb = this.platform.is('core');
    this.operation = this.navParams.get('operation');
    this.type = this.navParams.get('type');

    if (!this.dailyLogDate) {
      this.dailyLogDate = await this.dailyLogService.getCurrentLogDay();
    }

    this.lockedDayPermissions = await this.permissionService.lockedDayPermissions(this.dailyLogDate);

    if (this.config.isImplementerEnvironment) {
      this.endOfDay = await this.dateService.getEndOfDayLocally(this.dailyLogDate.dateTime);
    }

    const id = await this.operationService.getProjectSpecificOperationTempId();
    this.operationInstance.projectSpecificOperation = this.operation;
    this.operationInstance.projectSpecificOperationId = this.operation.id;
    this.operationInstance.localId = id;
    this.operationInstance.isConcurrentOperation = this.operation.defaultIsConcurrentOperation;

    if (this.operation.operationSummary.stepCollection) {
      this.operationInstance.stepCollectionInstance = this.operationService
        .createStepCollectionInstance(this.operation.operationSummary.stepCollection);
      this.operationInstance.stepCollectionInstance.stepCollectionId = this.operation.operationSummary.stepCollection.id;
    }

    if (this.type === OperationType.Concurrent) {
      this.operationInstance.isConcurrentOperation = true;
    } else if (this.type === OperationType.Interruption) {
      this.operationInstance.isInterruption = true;
    } else if (this.type === OperationType.Contractual) {
      this.operationInstance.isConcurrentOperation = false;
    } else if (this.type === OperationType.Delay) {
      this.operationInstance.isConcurrentOperation = false;
    }

    this.navHelperService.unsavedChanges = false;

    this.saved = false;
    const endOfDay = await this.dateService.getEndOfDayLocally(this.dailyLogDate.dateTime);

    await this.operationTimeService.setInitialTime(this.operationInstance.isConcurrentOperation, cloneDeep(this.dailyLogDate), endOfDay)
      .then(async (data) => {
        // if initial date goes into next day, then it means the user has filled the day with contractual operations, in this case - just set
        // the initial date as the start of the daily log date, (even though in theory they wouldn't need to add another if that was the case)
        if (data.ptLuxonDate.dateTime.toMillis() > endOfDay.dateTime.toMillis()) {
          data.ptLuxonDate = cloneDeep(this.dailyLogDate);
        }

        // it's possible offset may be null here if startTime has been set using the daily log date and not the previous op end time
        if (data.ptLuxonDate.offsetNull()) {
          const latestTimeTz = await this.timeZoneService.getEditorLocally({
            dateTime: data.ptLuxonDate.dateTime,
            dateAmbiguous: false
          });

          if (latestTimeTz) {
            data.ptLuxonDate.setOffset(latestTimeTz.secondsOffsetFromUtc);
          }
        }

        // data.ptLuxonDate.date will be UTC - systemOffset
        const ptLuxonDate = cloneDeep(data.ptLuxonDate) as PtLuxonDate;
        this.firstTime = data.first;
        await this.updateDifferentDayOperationFlag(cloneDeep(ptLuxonDate.dateTime));

        this.operationInstance.startTime = new PtLuxonDate();
        this.operationInstance.startTime.setDate(null);
        this.operationInstance.endTime = new PtLuxonDate();
        this.operationInstance.endTime.setDate(null);
        this.mostRecentTime = new PtLuxonDate();
        this.mostRecentTime.setDate(null);

        if (this.type !== OperationType.Delay && this.type !== OperationType.Interruption) {
          this.operationInstance.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(ptLuxonDate);
          this.operationInstance.startTime = ptLuxonDate;
          this.mostRecentTime = ptLuxonDate;
          this.operationInstance = await this.operationTimeService.setDefaultTimings(this.operationInstance);
        } else {
          this.operationInstance = this.operationTimeService.setNullTimings(this.operationInstance);
        }

        // await this.calculateOperationOffsets(this.operationInstance);

        this.resetOperation();

        this.min = this.operationInstance.startTime.dateTime
          ? this.dateService.selectEarliestDate(this.operationInstance.startTime.dateTime, this.dailyLogDate.dateTime)
          : this.dailyLogDate.dateTime;
        this.max = await this.dateService.addDuration(this.min, {day: 1}, true);

        if (!this.mostRecentTime.getDate()) {
          this.mostRecentTime.setDate(DateTime.local());
        }

        await this.calculateOperationOffsets(this.operationInstance);
        await this.checkOverlaps();
        this.multipleDaysExist = await this.operationValidatorService.multipleDaysExist(this.operationInstance);
        this.setTypes();
      });

    this.canConfirmOperationTimes = !this.jackingActivitySerivce.operationRequiresJackingActivity(this.operationInstance)
      && !this.onSiteMovementService.operationRequiresOnSiteMovementRecord(this.operationInstance);

    // TODO-RYAN: Put this back in once SOS-1080/SOS-1081 are done
    // !this.jackingActivitySerivce.operationRequiresJackingActivity(this.operationInstance);
    if (!!this.operationInstance.stepCollectionInstance) {
      for (const step of this.operationInstance.stepCollectionInstance.stepInstances) {
        step.canConfirmTimes = this.canConfirmStepTime(step);
      }
    }

    this.loading = false;
  }

  async initViewEnter() {
    this.navHelperService.warned = false;
    this.navHelperService.showWarning = false;

    this.navHelperService.triedToSave = false;
    this.popFn = this.navCtrl.pop;

    for (const element of this.operationInstance.switchTimes) {
      await this.stepService.splitStepsAtTime(this.operationInstance, element.date);
    }

    await this.stepService.attemptJoinStepsInOperation(this.operationInstance);

    this.swapMessage = this.operationInstance.isConcurrentOperation
      ? 'Change to contractual'
      : 'Change to concurrent';

    this.navCtrl.pop = (): Promise<any> => {
      this.navHelperService.triedToSave = true;

      if (this.navHelperService.warned || this.navHelperService.unsavedChanges === false) {
        this.navHelperService.unsavedChanges = false;
        this.navCtrl.pop = this.popFn;
        this.navCtrl.pop();
        return;
      }

      this.navHelperService.showWarning = true;
      this.navHelperService.warned = true;
      this.navCtrl.pop = this.popFn;
      return;
    };

    if (this.operationInstance != null && this.mostRecentTime != null) {
      // await this.calculateOperationOffsets(this.operationInstance);
    }

    // call this here so that concurrent/contractual tags update after adding a switch
    this.setTypes();
  }

  ionViewDidLeave() {
    this.navHelperService.warned = true;
    this.navHelperService.showWarning = false;

    this.navCtrl.pop = this.popFn;
  }

  openDailyLogModal() {
    const dailyLogModal = this.modalController.create(DailyLogModalPage);
    dailyLogModal.present();
  }

  openErrorModal() {
    const errorModal = this.modalController.create(ErrorsComponent, {
      validationResult: this.validationResult,
      refresh: true
    });
    errorModal.present();
  }

  openRemoveCommentModal() {
    const removeCommentModal = this.modalController.create(RemoveCommentComponent, {
      comment: this.operationInstance.stepCollectionInstance.stepInstances[this.lastStepPosition].comment.data,
      message: 'The below comment is attached to the step you are about to remove. Do you want to '
        + 'remove the comment as well or keep the comment against the operation?',
      trueText: 'Remove Comment',
      falseText: 'Keep Comment'
    });

    removeCommentModal.onDidDismiss(data => {

      const stepInstance = this.operationInstance.stepCollectionInstance.stepInstances[this.lastStepPosition];

      if (!data.choice) {
        const logComment = new LogComment();
        logComment.startTimeToShow = stepInstance.startTimeToShow;

        const comment = new LogComment();
        comment.data = stepInstance.comment.data;
        comment.appear = stepInstance.comment.appear;
        comment.milestone = stepInstance.comment.milestone;
        logComment.data = comment.data;
        logComment.milestone = comment.milestone;
        logComment.startTimeToShow = stepInstance.startTimeToShow;
        logComment.startTimeValid = stepInstance.startTimeToShow != null;
        logComment.startTimeDate = stepInstance.startTime;
        logComment.startTimeOffset = stepInstance.startTimeOffset;

        if (!this.operationInstance.comments) {
          this.operationInstance.comments = [];
        }

        this.operationInstance.comments.push(logComment);
      }

      stepInstance.comment = null;

      this.toggleExclude(stepInstance, this.lastStepPosition);

    });

    removeCommentModal.present();
  }

  goToGenericOperationComments() {
    this.navHelperService.unsavedChanges = true;
    this.navCtrl.push(OperationGenericCommentsPage, {
      operationInstance: this.operationInstance,
      dailyLogDate: this.dailyLogDate,
      lockedDayPermissions: this.lockedDayPermissions
    });
  }

  goToOperationStartComment() {
    this.navHelperService.unsavedChanges = true;
    this.navCtrl.push(OperationCommentsPage, {
      operationInstance: this.operationInstance,
      isStart: true,
      lockedDayPermissions: this.lockedDayPermissions
    });
  }

  goToOperationEndComment() {
    this.navHelperService.unsavedChanges = true;
    this.navCtrl.push(OperationCommentsPage, {
      operationInstance: this.operationInstance,
      isStart: false,
      lockedDayPermissions: this.lockedDayPermissions
    });
  }

  goToOperationSplitPage() {
    this.navCtrl.push(OperationSplitPage, {
      instance: this.operationInstance,
      dailyLogDate: this.dailyLogDate.dateTime,
      callback: this.navHelperService.dataSavedCallback
    });
  }

  goToStepComment(stepInstance?: StepInstance) {
    this.navHelperService.unsavedChanges = true;
    this.navCtrl.push(StepCommentsPage, {
      stepInstance: stepInstance,
      lockedDayPermissions: this.lockedDayPermissions
    });
  }

  toggleStartConfirmStep(stepInstance: StepInstance) {
    if (stepInstance.canConfirmTimes) {
      this.navHelperService.unsavedChanges = true;
      stepInstance.startConfirmed = !stepInstance.startConfirmed;
    }
  }

  toggleEndConfirmStep(stepInstance: StepInstance) {
    if (stepInstance.canConfirmTimes) {
      this.navHelperService.unsavedChanges = true;
      stepInstance.endConfirmed = !stepInstance.endConfirmed;
    }
  }

  toggleStartConfirmOp(operationInstance: DailyLogOperationInstance) {
    if (this.canConfirmOperationTimes) {
      this.navHelperService.unsavedChanges = true;
      operationInstance.startConfirmed = !operationInstance.startConfirmed;
    }
  }

  toggleEndConfirmOp(operationInstance: DailyLogOperationInstance) {
    if (this.canConfirmOperationTimes) {
      this.navHelperService.unsavedChanges = true;
      operationInstance.endConfirmed = !operationInstance.endConfirmed;
    }
  }


  async toggleExclude(stepInstance: StepInstance, position: number) {
    this.navHelperService.unsavedChanges = true;

    if (!!stepInstance.comment) {
      this.lastStepPosition = position;
      this.openRemoveCommentModal();
      return;
    }

    position++; // need to add 1 for the below adjustAllEstimates() to work (with regard to the bug where stepDelay is increased when editing a time - SOS-1561)

    this.navHelperService.unsavedChanges = true;
    stepInstance.exclude = !stepInstance.exclude;

    stepInstance.startConfirmed = false;
    stepInstance.endConfirmed = false;

    const latestTime = this.operationInstance.startTime;

    this.operationInstance = await this.operationTimeService
      .updateFutureSteps(0, latestTime, this.operationInstance,
        this.operationInstance.stepCollectionInstance.stepInstances,
        false);

    // await this.multipleDayService.adjustDays(this.operationInstance);

    await this.multipleDayService.workOutEndDate(this.operationInstance);

    this.operationTimeService.adjustAllEstimates(this.operationInstance, position);

    await this.calculateOperationOffsets(this.operationInstance);

    await this.validateOperation(this.mostRecentTime);

    this.setTypes();
  }

  confirm(type: DailyLogType, position?: number, end?: boolean) {

    switch (type) {
      case DailyLogType.OperationStart:
        if (this.canConfirmOperationTimes) {
          this.operationInstance.startConfirmed = true;
        }
        break;
      case DailyLogType.OperationEnd:
        if (this.canConfirmOperationTimes) {
          this.operationInstance.endConfirmed = true;
        }
        break;
      case DailyLogType.Step:

        if (position == null || position === undefined) {
          break;
        }
        if ((this.operationInstance.stepCollectionInstance.stepInstances[position]).canConfirmTimes) {
          if (!end) {
            this.operationInstance.stepCollectionInstance.stepInstances[position].startConfirmed = true;
          } else {
            this.operationInstance.stepCollectionInstance.stepInstances[position].endConfirmed = true;
          }
        }
        break;
    }
  }

  async startChanged(position: number, stepInstance?: StepInstance, datePicker?: boolean, dateTime?: DateTime) {
    let allowed = false;

    if (!stepInstance) {
      // if user previously nulled the time and is now re-entering one, then startTime.date will be null,
      // the process of updating future step times wont work until they also select a date
      if (this.operationInstance.startTime && !this.operationInstance.startTime.dateTime) {
        // need the || here to ensure the process works as intended whichever way the user fills out the step, either datepicker or time
        // input first
        this.operationInstance.startTime.dateTime = dateTime || this.operationInstance.startTime.dateTime;
      }

      allowed = await this.dateService.timeAllowed(
        this.operationInstance.startTimeToShow,
        this.operationInstance.startTime && this.operationInstance.startTime.dateTime ? this.operationInstance.startTime.dateTime : this.dailyLogDate.dateTime
      );
    } else {
      if (datePicker && !dateTime) {
        stepInstance.startTime.dateTime = null;
        stepInstance.startTimeToShow = null;
      }

      if (stepInstance.startTime && !stepInstance.startTime.dateTime) {
        stepInstance.startTime.dateTime = dateTime || this.operationInstance.startTime.dateTime;
      }

      allowed = await this.dateService.timeAllowed(stepInstance.startTimeToShow, stepInstance.startTime.dateTime
        ? stepInstance.startTime.dateTime
        : this.dailyLogDate.dateTime);
    }

    if (!allowed) {
      return;
    }

    const startTimeCopy = cloneDeep(this.operationInstance.startTime);

    this.navHelperService.unsavedChanges = true;

    let latestTime = this.operationInstance.startTime;

    // operation start time changed
    if (!stepInstance) {
      this.operationInstance.startTime = await this.dateService.setPtLuxonDateFromUserEnteredString(
        this.operationInstance.startTimeToShow,
        this.operationInstance.startTime && this.operationInstance.startTime.dateTime ? this.operationInstance.startTime.dateTime : this.dailyLogDate.dateTime);

      if (this.canConfirmOperationTimes) {
        this.operationInstance.startConfirmed = true;
      }

      if (this.operationInstance.startTime.dateTime > this.operationInstance.cancelledTime) {
        this.operationInstance.cancelledTime = null;
      }

      if (this.operationInstance.startTime.dateTime == null) {
        await this.validateOperation();
        this.operationTimeService.adjustAllEstimates(this.operationInstance, position);
        await this.calculateOperationOffsets(this.operationInstance);
        this.setTypes();
      }

      latestTime = this.operationInstance.startTime;
      if (this.canConfirmOperationTimes) {
        this.operationInstance.startConfirmed = true;
      }
    } else {
      if (stepInstance.canConfirmTimes) {
        stepInstance.startConfirmed = true;
      }

      await this.validateOperation();

      const previousTime = this.operationService
        .getPreviousTimeFromSteps(this.operationInstance.stepCollectionInstance, stepInstance) || this.operationInstance.startTime.dateTime;

      // set date based on user time input
      stepInstance.startTime = await this.dateService
        .setPtLuxonDateFromUserEnteredString(stepInstance.startTimeToShow,
          datePicker
            ? dateTime
            : stepInstance.startTime.dateTime || previousTime || this.dailyLogDate.dateTime);

      latestTime = stepInstance.startTime;

      // operation set open ended
      if (!stepInstance.startTime || !stepInstance.startTime.getDate()) {
        this.operationTimeService
          .removeTime(this.operationInstance.stepCollectionInstance.stepInstances, this.operationInstance, stepInstance, position);
      }

      if (stepInstance) {
        position++;
      }

      // if time is smaller, entered date is assumed to be on the next day
      if (!datePicker && stepInstance.startTime && stepInstance.startTime.dateTime && previousTime
        && stepInstance.startTime.dateTime.toMillis() < previousTime.toMillis()) {
        stepInstance.startTime = await this.dateService.incrementDay(stepInstance.startTime);
      }
    }

    // only update future times if we know the offset for the time entered - if we don't then these will be updated when the user selects the offset
    if ((stepInstance
      ? stepInstance.startTime.dateTime && stepInstance.startTime.offsetNotNull() // if user nulls step we still want to run the below
      : this.operationInstance.startTime)
      && this.operationInstance.startTime.dateTime && this.operationInstance.startTime.offsetNotNull()) {

      if (!!this.operationInstance.stepCollectionInstance) {
        this.operationInstance = await this.operationTimeService
          .updateFutureSteps(position,
            latestTime,
            this.operationInstance,
            this.operationInstance.stepCollectionInstance.stepInstances,
            true,
            null,
            stepInstance ? stepInstance.canConfirmTimes : null);
      }

      await this.multipleDayService.workOutEndDate(this.operationInstance, null, null, null, startTimeCopy);
      this.operationTimeService.adjustAllEstimates(this.operationInstance, position);

      this.setTypes();
    }

    await this.validateOperation();
  }

  async endChanged(position: number, stepInstance?: StepInstance, datePicker?: boolean, dateTime?: DateTime) {
    let allowed = false;
    if (!stepInstance) {
      if (datePicker && !dateTime) {
        this.operationInstance.endTime.dateTime = null;
        this.operationInstance.endTimeToShow = null;
      }

      allowed = await this.dateService.timeAllowed(
        this.operationInstance.endTimeToShow,
        this.operationInstance.endTime && this.operationInstance.endTime.dateTime ? this.operationInstance.endTime.dateTime : this.dailyLogDate.dateTime
      );
    } else {
      if (datePicker && !dateTime) {
        stepInstance.endTime.dateTime = null;
        stepInstance.endTimeToShow = null;
      }

      if (stepInstance.endTime && !stepInstance.endTime.dateTime) {
        // need the || here to ensure the process works as intended whichever way the user fills out the step, either datepicker or time
        // input first
        stepInstance.endTime.dateTime = dateTime || stepInstance.endTime.dateTime;
      }

      allowed = await this.dateService.timeAllowed(stepInstance.endTimeToShow, stepInstance.endTime.dateTime
        ? stepInstance.endTime.dateTime
        : this.dailyLogDate.dateTime);
    }

    if (!allowed) {
      return;
    }

    const startTimeCopy = cloneDeep(this.operationInstance.startTime);
    this.operationInstance.endTime = await this.dateService.setPtLuxonDateFromUserEnteredString(
      this.operationInstance.endTimeToShow,
      datePicker
        ? dateTime
        : this.operationInstance.endTime && this.operationInstance.endTime.dateTime
          ? this.operationInstance.endTime.dateTime
          : this.dailyLogDate.dateTime);

    this.navHelperService.unsavedChanges = true;

    let latestTime = this.operationInstance.startTime;

    // operation end time changed
    if (!stepInstance) {
      if (this.canConfirmOperationTimes) {
        this.operationInstance.endConfirmed = true;
      }
    } else {
      const previousTime = this.operationService
        .getPreviousTimeFromSteps(this.operationInstance.stepCollectionInstance, stepInstance) || this.operationInstance.startTime.dateTime;

      // set date based on user time input
      stepInstance.endTime = await this.dateService
        .setPtLuxonDateFromUserEnteredString(stepInstance.endTimeToShow,
          datePicker
            ? dateTime
            : stepInstance.endTime.dateTime || stepInstance.startTime.dateTime || previousTime);

      // operation set open ended
      if (stepInstance.endTime.dateTime == null) {
        this.operationTimeService
          .removeTime(this.operationInstance.stepCollectionInstance.stepInstances, this.operationInstance, stepInstance, position);
        await this.validateOperation();
        await this.multipleDayService.workOutEndDate(this.operationInstance, null, null, null, startTimeCopy);
        await this.calculateOperationOffsets(this.operationInstance);
        this.setTypes();
        return;
      }

      // if time is smaller, entered date is assumed to be on the next day
      if (!datePicker && stepInstance.endTime.dateTime.toMillis() < previousTime.toMillis()) {
        stepInstance.endTime = await this.dateService.incrementDay(stepInstance.endTime);
      }

      if (stepInstance.canConfirmTimes) {
        stepInstance.endConfirmed = true;
      }

      position++;

      latestTime = stepInstance.endTime;
    }

    // only update future times if we know the offset for the time entered - if we don't then these will be updated when the user selects the offset
    if ((stepInstance
      ? (stepInstance.endTime.dateTime && stepInstance.endTime.offsetNotNull() || !stepInstance.endTime.dateTime) // if user nulls step we still want to run the below)) {
      : this.operationInstance.endTime.dateTime && this.operationInstance.endTime.offsetNotNull())) {

      if (this.operationInstance.stepCollectionInstance && stepInstance) {
        this.operationInstance = await this.operationTimeService
          .updateFutureSteps(position,
            latestTime,
            this.operationInstance,
            this.operationInstance.stepCollectionInstance.stepInstances,
            false);
      }

      await this.multipleDayService.workOutEndDate(this.operationInstance, true, null, null, startTimeCopy, true, !stepInstance);
      this.operationTimeService.adjustAllEstimates(this.operationInstance, position);

      this.setTypes();
    }

    await this.validateOperation();
  }

  /* Gets called only once when the value is complete, as opposed to startChange() which gets called on each character entered */
  async startValueChanged(position: number, stepInstance?: StepInstance) {
    let valid = false;
    if (!stepInstance) {
      valid = this.dateService.timeIsValid(this.operationInstance.startTimeToShow);
    } else {
      valid = this.dateService.timeIsValid(stepInstance.startTimeToShow);
    }

    if (!valid) {
      if (!stepInstance) {
        this.operationInstance.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(this.operationInstance.startTime);
      } else {
        this.operationInstance.stepCollectionInstance.stepInstances[position].startTimeToShow
          = this.dateService.setTimeStringFromPtLuxonDate(this.operationInstance.stepCollectionInstance.stepInstances[position].startTime);
      }
    }
  }

  /* Gets called only once when the value is complete, as opposed to endChange() which gets called on each character entered */
  async endValueChanged(position: number, stepInstance?: StepInstance) {
    let valid = false;
    if (!stepInstance) {
      valid = this.dateService.timeIsValid(this.operationInstance.endTimeToShow);
    } else {
      valid = this.dateService.timeIsValid(stepInstance.endTimeToShow);
    }

    if (!valid) {
      if (!stepInstance) {
        // this.operationInstance.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(this.operationInstance.endTime.date);
      } else {
        // this.operationInstance.stepCollectionInstance.stepInstances[position].endTimeToShow
        //   = this.dateService.setTimeStringFromPtLuxonDate(this.operationInstance.stepCollectionInstance.stepInstances[position].endTime.date);
      }
    }
  }

  async openLocationSelect() {
    if (!await this.validateResponsibilityForSave()) {
      return;
    }

    await this.navCtrl.push(OperationDetailsLocationSelectPage,
      {
        instance: this.operationInstance, stepInstances: this.operationInstance.stepCollectionInstance
          ? this.operationInstance.stepCollectionInstance.stepInstances
          : null,
        dailyLogDate: this.dailyLogDate
      });
  }

  private async handleSplitSteps() {
    const midnight = await this.dateService.getEndOfDayLocally(this.dailyLogDate.dateTime);
    await this.stepService.splitStepsAtTime(this.operationInstance, midnight, true);

    for (let i = 0; i < this.operationInstance.switchTimes.length; i++) {
      await this.stepService.splitStepsAtTime(this.operationInstance, this.operationInstance.switchTimes[i].date);
    }

    await this.stepService.attemptJoinStepsInOperation(this.operationInstance);

    // need to ensure types are up to date after any potentially splitting
    this.setTypes();
  }

  async save() {
    this.loading = true;

    if (!await this.validateResponsibilityForSave()) {
      this.loading = false;
      return;
    }

    await this.handleSplitSteps();

    if (this.type === OperationType.Delay || this.type === OperationType.Interruption) {
      await this.navCtrl.push(AffectedOperationsPage, {
        instance: this.operationInstance,
        type: this.type,
        dailyLogDate: this.dailyLogDate
      });
      this.loading = false;
      return;
    }

    if (!!this.saved) {
      this.loading = false;
      return;
    }

    const saveRequest: OperationInstanceSaveRequest = {
      operationInstanceEditor: cloneDeep(this.operationInstance) as DailyLogOperationInstance,
      dailyLogDate: cloneDeep(this.dailyLogDate.dateTime) as DateTime,
    };

    await this.operationSaveService.saveOperationInstance(saveRequest)
      .then(async savedInstance => {

        // todo we need to sync local app data here i think, with the updated saved operation/records data
        this.navHelperService.unsavedChanges = false;
        this.saved = true;
        this.navHelperService.warned = true;

        this.presentSaveToast().then(() => {
          this.navCtrl.setRoot(DailyLogPage);
          this.navHelperService.updateNavMenuPage({page: NavMainMenuPage});
        });
      }, async error => {
        this.loading = false;
        throw (error);
      });
  }

  private resetOperation() {
    this.operationInstance.startComment = null;
    this.operationInstance.endComment = null;
    this.operationInstance.comments = null;

    if (!this.operationInstance.stepCollectionInstance) {
      return;
    }

    this.operationInstance.stepCollectionInstance.stepInstances.forEach(element => {
      element.exclude = false;
      element.endConfirmed = false;
      element.startConfirmed = false;
      element.comment = null;
    });

    this.operationInstance.endConfirmed = false;
    this.operationInstance.startConfirmed = false;
  }

  regexChange(inputTime: string) {
    if (inputTime.charAt(0) === '2') {
      this.mask[1] = new RegExp('[0-3]');
    } else {
      this.mask[1] = new RegExp('\\d');
    }
  }

  presentSaveToast(): Promise<any> {
    const toast = this.toastCtrl.create({
      message:
        `${this.operationInstance.isConcurrentOperation ? 'Concurrent ' : 'Contractual '}`
        + 'Operation \''
        + `${this.operation.name ? this.operation.name : this.operation.operationSummary.name}`
        + '\' added to the Daily Log',
      duration: 6000,
      position: 'bottom'
    });

    return toast.present();
  }

  setTypes() {
    if (!!this.operationInstance.startTime) {
      this.operationInstance.startConcurrent = this.timeType(this.operationInstance.startTime.getDate());
    }

    if (!!this.operationInstance.endTime) {
      this.operationInstance.endConcurrent = this.timeType(this.operationInstance.endTime.getDate());
    }

    const steps = !this.operationInstance.stepCollectionInstance
      ? []
      : this.operationInstance.stepCollectionInstance.stepInstances;

    steps.forEach(element => {
      if (!!element.startTime) {
        element.isConcurrent = this.timeType(element.startTime.getDate());
      }
    });

    this.badgesLoaded = true;
  }

  timeType(date: DateTime): boolean {
    if (!this.operationInstance.projectSpecificOperation) {
      return false;
    }

    let switchTimes = [];

    if (!date) {
      switchTimes = this.operationInstance.switchTimes;
    } else {
      switchTimes = this.operationInstance.switchTimes.filter(p => p.date.dateTime <= date);
    }

    if (switchTimes.length < 1) {
      return this.operationInstance.isConcurrentOperation;
    }

    let switchTime = switchTimes[0];

    switchTimes.forEach(time => {
      if (time.date.dateTime > switchTime.date.getDate()) {
        switchTime = time;
      }
    });

    return switchTime.isConcurrent;
  }

  async swapOperationClass() {
    if (!this.operationInstance) {
      return;
    }

    this.navHelperService.unsavedChanges = true;
    this.operationInstance.isConcurrentOperation = !this.operationInstance.isConcurrentOperation;

    this.swapMessage = this.operationInstance.isConcurrentOperation
      ? 'Change to contractual'
      : 'Change to concurrent';

    this.operationInstance.switchTimes.forEach(element => {
      this.stepService.splitStepsAtTime(this.operationInstance, element.date);
    });

    await this.stepService.attemptJoinStepsInOperation(this.operationInstance);

    this.setTypes();

    await this.calculateOperationOffsets(this.operationInstance);
    await this.validateOperation(this.mostRecentTime);
  }

  async updateDifferentDayOperationFlag(operationStartDate: DateTime) {
    if (!operationStartDate || this.dailyLogDate || !this.dailyLogDate.dateTime) {
      this.differentDayOperation = false;
      return;
    }

    this.differentDayOperation = !(this.dateService.compareDayOffsetsPreApplied(operationStartDate, this.dailyLogDate.dateTime));
  }

  private canConfirmStepTime(stepInstance: StepInstance) {
    return this.canConfirmOperationTimes || !stepInstance.step.isSystemDefault;
  }

  private async refreshOperationValidationSettings(): Promise<void> {
    this.loading = true;
    this.operationValidationSettings = null;
    this.saveValidationIsValid = false;
    this.validationResult = new ValidationResult();
    try {
      this.operationValidationSettings = await this.operationService.getOperationValidationSettings(
        this.operationInstance, this.dailyLogDate);
      await this.validateOperation();
    } catch (error) {
      this.operationValidationSettings = null;
      this.saveValidationIsValid = false;
      await this.toastCtrl.create({
        message: 'Unable to load operation settings for this reporting day. Please reopen the operation to try again.',
        duration: 6000,
        position: 'bottom'
      }).present();
    } finally {
      this.loading = false;
    }
  }

  private async validateOperation(mostRecentTime?: PtLuxonDate) {
    if (!this.operationValidationSettings) {
      return;
    }
    this.validationResult = await this.operationValidatorService.validateOperation(
      cloneDeep(this.operationInstance),
      cloneDeep(mostRecentTime || this.operationInstance.startTime), cloneDeep(this.dailyLogDate), null, null);
    await this.checkOverlaps();
    await this.checkLockedDayDateRestrictions();
    this.responsibilityRequired = await this.operationService.isResponsibilityRequired(this.operationInstance, this.dailyLogDate, this.operationValidationSettings);
    this.saveValidationIsValid = this.validationResult.isValid;
    this.addResponsibilityValidationError();
    this.multipleDaysExist = await this.operationValidatorService.multipleDaysExist(this.operationInstance);
  }

  get responsibilityInvalid(): boolean {
    return this.responsibilityRequired && this.responsibilityValidationAttempted && !this.responsibilityIsValid();
  }

  responsibilityRequired: boolean = true;

  async responsibilityChanged() {
    this.navHelperService.unsavedChanges = true;

    await this.validateOperation();
  }

  private async validateResponsibilityForSave(): Promise<boolean> {
    if (!this.operationValidationSettings) {
      return false;
    }
    await this.validateOperation();
    this.responsibilityValidationAttempted = this.responsibilityRequired;
    return !this.responsibilityInvalid;
  }

  private addResponsibilityValidationError() {
    if (this.responsibilityRequired && !this.responsibilityIsValid()) {
      this.validationResult.addError(
        'Responsibility must be selected as either Client or Owner.',
        ErrorType.Error
      );
    }
  }

  private responsibilityIsValid(): boolean {
    return this.operationInstance.responsibilityTypeId === ResponsibilityType.Client
      || this.operationInstance.responsibilityTypeId === ResponsibilityType.Owner;
  }

  // needs to exist here instead of in operationValidatorService due to circular dependency issue
  private async checkOverlaps() {
    if (this.operationInstance != null) {
      const instances = await this.operationService.getAllProjectSpecificOperationInstancesForDay(this.dailyLogDate, this.operationInstance.id);

      const index = instances.findIndex(e => e.id && this.operationInstance.id
        ? e.id === this.operationInstance.id
        : e.localId !== null && this.operationInstance.localId !== null && e.localId === this.operationInstance.localId);
      // apply in-app offsets for the below comparison, as offsets will be applied to other later objects in .findOverlapTimes()
      const operationInstanceCopy = cloneDeep(this.operationInstance);

      if (index !== -1) {
        instances[index] = operationInstanceCopy;
      } else {
        instances.push(operationInstanceCopy);
      }

      let overlaps = await this.dailyLogValidationService.findOverlapTimes(instances, this.dailyLogDate);

      // filter found overlaps to only show if they include the current operation being edited - we don't care about the others on this screen
      overlaps = overlaps.filter(e => e.previousOperation.localId != null && e.previousOperation.localId === operationInstanceCopy.localId);

      for (const overlap of overlaps) {
        overlap.type = ActionType.OperationOverlap;

        if (overlap.previousOperation) {
          overlap.previousOperation.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(overlap.previousOperation.startTime);
          overlap.previousOperation.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(overlap.previousOperation.endTime);

          // todo - check needed
          //await overlap.previousOperation.startTime.setLocalDate(this.timeZoneService, false, null, overlap.previousOperation.startTimeOffset);
          //await overlap.previousOperation.endTime.setLocalDate(this.timeZoneService, false, null, overlap.previousOperation.endTimeOffset);

          if (overlap.previousOperation.id != null && overlap.previousOperation.id === operationInstanceCopy.id) {
            overlap.previousOperation = operationInstanceCopy;
          }
        }

        if (overlap.operationInstance) {
          overlap.operationInstance.startTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(overlap.operationInstance.startTime);
          overlap.operationInstance.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(overlap.operationInstance.endTime);

          // dailyLogValidationService.findOverlapTimes is a massive mess, so after that has run, just replace overlap.operationInstance if it matches the operation id
          // that's currently being edited. So we don't have to try and insert this operation into that method. (because in that service the current operation is getting
          // overwritten somewhere - so the error message has the wrong time in it)
          if (overlap.operationInstance.id != null && overlap.operationInstance.id === operationInstanceCopy.id) {
            overlap.operationInstance = operationInstanceCopy;
          }
        }

        this.validationResult.addError(
          await this.dailyLogValidationService.buildStandardOverlapMessage(
            overlap, this.dailyLogDate.dateTime, 'Saving this operation will cause an overlap between', 'End of Day'),
          ErrorType.Warning,
          null,
          overlap);
      }

      await this.calculateOperationOffsets(this.operationInstance);
    }
  }

  async calculateOperationOffsets(instance: DailyLogOperationInstance) {
    if (!instance) {
      return;
    }

    instance.startTimeOffset = instance.startTime.dateTime && instance.startTime.offsetNotNull()
      ? instance.startTime.dateTime.offset / 60
      : null;

    if (!!instance.stepCollectionInstance) {
      for (const stepInstance of instance.stepCollectionInstance.stepInstances) {
        if (!!stepInstance.startTime) {
          stepInstance.startTimeOffset = stepInstance.startTime.dateTime && stepInstance.startTime.dateTime.offset
            ? stepInstance.startTime.dateTime.offset / 60
            : NaN;
        }

        if (!!stepInstance.endTime) {
          stepInstance.endTimeOffset = stepInstance.endTime.dateTime != null && stepInstance.endTime.offsetNotNull()
            ? stepInstance.endTime.dateTime.offset / 60
            : NaN;
        }
      }
    }

    instance.endTimeOffset = instance.endTime.dateTime && instance.endTime.offsetNotNull()
      ? instance.endTime.dateTime.offset / 60
      : NaN;
  }

  async offsetSelected(ptDate: PtLuxonDate, startChanged: boolean, position: number, stepInstance?: StepInstance) {
    // this method will be called when offset is nulled by startChanged/endChanged, so just return here if so
    if (ptDate.multipleOffsets && ptDate.multipleOffsets.selectedOffsetMinutes === null) {
      return;
    }

    // instance start time offset changed
    if (startChanged && !stepInstance) {
      await this.instanceStartOffsetChanged(ptDate);
      await this.validateOperation();

      return;
    }

    // instance end time offset changed
    if (!startChanged && !stepInstance) {
      await this.instanceEndOffsetChanged(ptDate);
      await this.validateOperation();

      return;
    }

    // steps
    if (stepInstance) {
      if (startChanged) {
        await this.stepStartOffsetChanged(ptDate, stepInstance, position);
        await this.validateOperation();
      } else {
        await this.stepEndOffsetChanged(ptDate, stepInstance, position);
        await this.validateOperation();
      }
    }
  }

  async instanceStartOffsetChanged(ptLuxonDate: PtLuxonDate) {
    this.operationInstance.startTimeOffset = ptLuxonDate.dateTime.offset / 60;
  }

  async instanceEndOffsetChanged(ptLuxonDate: PtLuxonDate) {
    this.operationInstance.endTimeOffset = ptLuxonDate.dateTime.offset / 60;
  }

  async stepStartOffsetChanged(ptLuxonDate: PtLuxonDate, stepInstance: StepInstance, position: number) {
    this.operationInstance.stepCollectionInstance.stepInstances[position].startTimeOffset = ptLuxonDate.dateTime.offset / 60;

    // if step is point in time step
    if (stepInstance.step.defaultSecondsSpan === 0) {
      this.operationInstance.stepCollectionInstance.stepInstances[position].endTime = this.operationInstance.stepCollectionInstance.stepInstances[position].startTime;
      this.operationInstance.stepCollectionInstance.stepInstances[position].endTimeOffset
        = this.operationInstance.stepCollectionInstance.stepInstances[position].startTimeOffset;
      // this.operationInstance.stepCollectionInstance.stepInstances[position].endTime.inAppOffset
      //   = this.operationInstance.stepCollectionInstance.stepInstances[position].startTime.inAppOffset;
      this.operationInstance.stepCollectionInstance.stepInstances[position].endTime.multipleOffsets
        = this.operationInstance.stepCollectionInstance.stepInstances[position].startTime.multipleOffsets;
      this.operationInstance.stepCollectionInstance.stepInstances[position].endTimeToShow
        = this.operationInstance.stepCollectionInstance.stepInstances[position].startTimeToShow;
    }
  }

  async stepEndOffsetChanged(ptLuxonDate: PtLuxonDate, stepInstance: StepInstance, position: number) {
    this.operationInstance.stepCollectionInstance.stepInstances[position].endTimeOffset = ptLuxonDate.dateTime.offset / 60;
  }

  // when operation page is loaded we set some flags for each operation time depending on if the times are on the next day
  // now if any times that were not already on the next day, are now moved onto the next day, and the user does not have permission to do so
  // when must stop them from being able to save
  private async checkLockedDayDateRestrictions() {
    if (this.config.isImplementerEnvironment && !this.lockedDayPermissions.functionalPermissions.canImpactFutureDay) {
      let openEnded = false;

      if (this.operationInstance.startTime && this.operationInstance.startTime.dateTime) {
        if (this.operationInstance.startTime.dateTime >= this.endOfDay.dateTime) {
          this.validationResult.addError(`You do not have permission to set the Operation Start time on a future day.`, ErrorType.Error);
        }
      }

      if (this.operationInstance.stepCollectionInstance && this.operationInstance.stepCollectionInstance.stepInstances) {
        for (let i = 0; i < this.operationInstance.stepCollectionInstance.stepInstances.length; i++) {
          const stepInstance = this.operationInstance.stepCollectionInstance.stepInstances[i];

          if (stepInstance.startTime && stepInstance.startTime.dateTime >= this.endOfDay.dateTime) {
            this.validationResult.addError(`You do not have permission to set Step <strong>${stepInstance.step.name}</strong> Start time on a future day.`, ErrorType.Error);
          } else if (!stepInstance.endTime || !stepInstance.endTime.dateTime) {
            openEnded = true;
          }

          if (stepInstance.secondsSpan !== 0 && stepInstance.endTime && stepInstance.endTime.dateTime >= this.endOfDay.dateTime) {
            this.validationResult.addError(`You do not have permission to set Step <strong>${stepInstance.step.name}</strong> End time on a future day.`, ErrorType.Error);
          } else if (!stepInstance.endTime || !stepInstance.endTime.dateTime) {
            openEnded = true;
          }
        }
      }

      if (this.operationInstance.endTimeToShow && this.operationInstance.endTime.dateTime) {
        if (this.operationInstance.endTime.dateTime >= this.endOfDay.dateTime) {
          this.validationResult.addError(`You do not have permission to set the Operation End time on a future day.`, ErrorType.Error);
        }
      } else if (!this.operationInstance.endTime || !this.operationInstance.endTime.dateTime) {
        openEnded = true;
      }

      if (openEnded) {
        this.validationResult.addError(`You do not have permission to leave the Operation open ended, as this would impact a future day.`, ErrorType.Error);
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/operations/operation-details/operation-details.ts